import React, { useState, useEffect, useMemo } from "react";
import { useSnackbar } from "notistack";
import AddUserModal from "../components/AddUserModal";
import { useLocation } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Typography,
  Box,
  Select,
  MenuItem,
  Button,
  FormControl,
  InputLabel,
  Avatar,
  Tooltip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  IconButton,
  Input,
  Chip,
  Switch,
  FormControlLabel,
} from "@mui/material";
import {
  CheckCircle,
  Edit,
  Delete,
  CloudUpload,
  ArrowUpward,
  ArrowDownward,
} from "@mui/icons-material";

// Tarihi okunabilir bir formata dönüştüren yardımcı fonksiyon
const formatDate = (timestamp) => {
  if (!timestamp) return "N/A";

  try {
    let date;

    // Backend artık ISO string gönderiyor
    if (typeof timestamp === "string") {
      date = new Date(timestamp);
    } else if (timestamp instanceof Date) {
      date = timestamp;
    } else if (timestamp.seconds) {
      // Fallback: Firestore Timestamp
      date = new Date(timestamp.seconds * 1000);
    } else {
      return "N/A";
    }

    // Invalid Date kontrolü
    if (isNaN(date.getTime())) return "N/A";

    return date.toLocaleDateString("tr-TR", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (e) {
    return "Geçersiz Tarih";
  }
};

function UserManagementPage() {
  const [selectedRoles, setSelectedRoles] = useState({});
  const [updatingRoles, setUpdatingRoles] = useState({});
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addUserModalOpen, setAddUserModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [updating, setUpdating] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();

  // Filtre/Sıralama/Arama durumları
  const [searchQuery, setSearchQuery] = useState("");
  const [filterBlocked, setFilterBlocked] = useState("all"); // all|3+|5+|7+
  const [filterEula, setFilterEula] = useState("all"); // all|accepted|missing
  const [filterEmailVerified, setFilterEmailVerified] = useState("all"); // all|verified|unverified
  const [sortBy, setSortBy] = useState("blockedCount"); // blockedCount|createdAt|emailVerified|eula
  const [sortDir, setSortDir] = useState("desc"); // asc|desc

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("add") === "true") {
      setAddUserModalOpen(true);
    }
  }, [location.search]);

  const loadUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const { apiCall } = await import("../config/api");
      const response = await apiCall("/api/admin/users");
      if (response.success) {
        const usersList = response.users || [];
        setUsers(usersList);

        const initialSelectedRoles = {};
        usersList.forEach((user) => {
          initialSelectedRoles[user.id] = user.role || "user";
        });
        setSelectedRoles(initialSelectedRoles);
      }
    } catch (err) {
      console.error("Error fetching users:", err);
      setError("Kullanıcılar yüklenirken bir hata oluştu: " + err.message);
    }
    setLoading(false);
  };

  // Filtrelenmiş ve sıralanmış kullanıcı listesi
  const displayedUsers = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const filtered = users.filter((u) => {
      const text = `${u.displayName || ""} ${u.email || ""} ${
        u.role || ""
      }`.toLowerCase();
      if (q && !text.includes(q)) return false;

      const bc = Number(u.blockedCount || 0);
      if (filterBlocked === "3+" && bc < 3) return false;
      if (filterBlocked === "5+" && bc < 5) return false;
      if (filterBlocked === "7+" && bc < 7) return false;

      const eula = u.eulaAcceptance || u.termsAcceptance;
      if (filterEula === "accepted" && !eula?.accepted) return false;
      if (filterEula === "missing" && !!eula?.accepted) return false;

      const ev = !!u.emailVerified;
      if (filterEmailVerified === "verified" && !ev) return false;
      if (filterEmailVerified === "unverified" && ev) return false;
      return true;
    });

    const dir = sortDir === "asc" ? 1 : -1;
    const sorted = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case "createdAt": {
          const av = a.createdAt?.seconds || 0;
          const bv = b.createdAt?.seconds || 0;
          return (av - bv) * dir;
        }
        case "emailVerified": {
          const av = a.emailVerified ? 1 : 0;
          const bv = b.emailVerified ? 1 : 0;
          return (av - bv) * dir;
        }
        case "eula": {
          const ae = (a.eulaAcceptance || a.termsAcceptance)?.accepted ? 1 : 0;
          const be = (b.eulaAcceptance || b.termsAcceptance)?.accepted ? 1 : 0;
          return (ae - be) * dir;
        }
        case "blockedCount":
        default: {
          const av = Number(a.blockedCount || 0);
          const bv = Number(b.blockedCount || 0);
          return (av - bv) * dir;
        }
      }
    });
    return sorted;
  }, [
    users,
    searchQuery,
    filterBlocked,
    filterEula,
    filterEmailVerified,
    sortBy,
    sortDir,
  ]);

  // CSV dışa aktarma (filtrelenmiş liste)
  const exportCSV = () => {
    const headers = [
      "id",
      "displayName",
      "email",
      "role",
      "emailVerified",
      "blockedCount",
      "isQuarantined",
      "eulaAccepted",
      "eulaAcceptedAt",
      "eulaIP",
      "eulaDevice",
    ];
    const rows = displayedUsers.map((u) => {
      const eula = u.eulaAcceptance || u.termsAcceptance || {};
      return [
        u.id || "",
        (u.displayName || "").replaceAll('"', '\\"'),
        (u.email || "").replaceAll('"', '\\"'),
        u.role || "",
        u.emailVerified ? "true" : "false",
        String(u.blockedCount || 0),
        u.isQuarantined ? "true" : "false",
        eula.accepted ? "true" : "false",
        eula.acceptedAt || "",
        eula.ipAddress || "",
        eula.deviceInfo || "",
      ];
    });
    const csv = [
      headers.join(","),
      ...rows.map((r) => r.map((v) => `"${String(v)}"`).join(",")),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `users_${new Date().toISOString()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const handleRoleChange = (userId, newRole) => {
    setSelectedRoles((prev) => ({ ...prev, [userId]: newRole }));
  };

  const handleUpdateRole = async (userId) => {
    const newRole = selectedRoles[userId];
    if (!newRole) return;

    setUpdatingRoles((prev) => ({ ...prev, [userId]: true }));
    setError(null);

    try {
      const { apiCall } = await import("../config/api");
      const response = await apiCall(`/api/admin/users/${userId}`, {
        method: "PATCH",
        body: JSON.stringify({ role: newRole }),
      });

      if (response.success) {
        setUsers((prevUsers) =>
          prevUsers.map((u) => (u.id === userId ? { ...u, role: newRole } : u))
        );
      } else {
        throw new Error(response.error || "Rol güncellenemedi");
      }
    } catch (err) {
      console.error("Error updating role:", err);
      setError(
        `Rol güncellenirken bir hata oluştu: ${err.message}. Lütfen tekrar deneyin.`
      );
    } finally {
      setUpdatingRoles((prev) => ({ ...prev, [userId]: false }));
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setEditFormData({
      displayName: user.displayName || "",
      email: user.email || "",
      photoURL: user.photoURL || "",
      role: user.role || "user",
      emailVerified: user.emailVerified || false,
      isManuallyQuarantined: user.isManuallyQuarantined || false,
    });
    setEditDialogOpen(true);
  };

  const handleResetBlocks = async () => {
    if (!editingUser) return;

    if (
      !window.confirm(
        `${editingUser.displayName} kullanıcısının tüm engellemelerini sıfırlamak istediğinizden emin misiniz? Bu işlem geri alınamaz.`
      )
    ) {
      return;
    }

    setUpdating(true);
    setError(null);

    try {
      const { apiCall } = await import("../config/api");
      const response = await apiCall(
        `/api/admin/users/${editingUser.id}/reset-blocks`,
        {
          method: "POST",
        }
      );

      if (response.success) {
        alert(`${response.deactivatedCount} engelleme sıfırlandı.`);
        await loadUsers(); // Refresh the entire user list
        setEditDialogOpen(false); // Close the dialog on success
      } else {
        throw new Error(response.error || "Engellemeler sıfırlanamadı");
      }
    } catch (err) {
      console.error("Error resetting blocks:", err);
      setError(`Engellemeler sıfırlanırken bir hata oluştu: ${err.message}`);
    } finally {
      setUpdating(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editingUser) return;

    setUpdating(true);
    setError(null);

    try {
      console.log("Sending edit data:", editFormData);
      const { apiCall } = await import("../config/api");
      const response = await apiCall(`/api/admin/users/${editingUser.id}`, {
        method: "PATCH", // Use PATCH to update only specified fields
        body: JSON.stringify(editFormData),
      });

      console.log("Edit response:", response);
      if (response.success) {
        setUsers((prevUsers) =>
          prevUsers.map((u) =>
            u.id === editingUser.id ? { ...u, ...editFormData } : u
          )
        );
        setEditDialogOpen(false);
        setEditingUser(null);
        setEditFormData({});
      } else {
        throw new Error(response.error || "Kullanıcı güncellenemedi");
      }
    } catch (err) {
      console.error("Error updating user:", err);
      setError(`Kullanıcı güncellenirken bir hata oluştu: ${err.message}`);
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Bu kullanıcıyı silmek istediğinizden emin misiniz?")) {
      return;
    }

    try {
      const { apiCall } = await import("../config/api");
      await apiCall(`/api/admin/users/${userId}`, {
        method: "DELETE",
      });

      // Kullanıcıyı listeden kaldır
      setUsers(users.filter((user) => user.id !== userId));
      enqueueSnackbar("Kullanıcı başarıyla silindi", { variant: "success" });
    } catch (error) {
      console.error("Kullanıcı silme hatası:", error);
      enqueueSnackbar("Kullanıcı silinirken bir hata oluştu", {
        variant: "error",
      });
    }
  };

  // User quarantine system removed for performance

  const handleDisableUser = async (userId) => {
    if (
      !window.confirm(
        "Bu kullanıcının hesabını durdurmak istediğinizden emin misiniz? Kullanıcı giriş yapamayacak."
      )
    ) {
      return;
    }

    try {
      const { apiCall } = await import("../config/api");
      await apiCall(`/api/admin/users/${userId}/disable`, {
        method: "POST",
      });

      enqueueSnackbar("Kullanıcı hesabı durduruldu", { variant: "success" });
      loadUsers(); // Refresh the user list
    } catch (error) {
      console.error("Error:", error);
      enqueueSnackbar("Bir hata oluştu", { variant: "error" });
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      setError("Lütfen geçerli bir resim dosyası seçin");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError("Resim dosyası 5MB'dan küçük olmalıdır");
      return;
    }

    setUploadingImage(true);
    setError(null);

    try {
      // Convert to base64 for simple storage
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64String = e.target.result;
        setEditFormData((prev) => ({ ...prev, photoURL: base64String }));
        setUploadingImage(false);
      };
      reader.onerror = () => {
        setError("Resim yüklenirken bir hata oluştu");
        setUploadingImage(false);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      console.error("Error uploading image:", err);
      setError("Resim yüklenirken bir hata oluştu");
      setUploadingImage(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "80vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ padding: 2 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
      <Paper
        elevation={3}
        sx={{
          p: 4,
          width: "100%",
          maxWidth: "1200px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography variant="h4" component="h1">
            Kullanıcı Yönetimi
          </Typography>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setAddUserModalOpen(true)}
            >
              Yeni Kullanıcı
            </Button>
            <Button
              variant="outlined"
              onClick={exportCSV}
              disabled={displayedUsers.length === 0}
            >
              CSV İndir
            </Button>
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            mb: 3,
            alignItems: "center",
          }}
        >
          <TextField
            label="Ara (İsim, E-posta, Rol)"
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ flexGrow: 1, minWidth: "200px" }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Engellenme</InputLabel>
            <Select
              value={filterBlocked}
              label="Engellenme"
              onChange={(e) => setFilterBlocked(e.target.value)}
            >
              <MenuItem value="all">Tümü</MenuItem>
              <MenuItem value="3+">3+ Engellenme</MenuItem>
              <MenuItem value="5+">5+ Engellenme</MenuItem>
              <MenuItem value="7+">7+ (Karantina)</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>EULA Durumu</InputLabel>
            <Select
              value={filterEula}
              label="EULA Durumu"
              onChange={(e) => setFilterEula(e.target.value)}
            >
              <MenuItem value="all">Tümü</MenuItem>
              <MenuItem value="accepted">Kabul Edildi</MenuItem>
              <MenuItem value="missing">Eksik</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>E-posta</InputLabel>
            <Select
              value={filterEmailVerified}
              label="E-posta"
              onChange={(e) => setFilterEmailVerified(e.target.value)}
            >
              <MenuItem value="all">Tümü</MenuItem>
              <MenuItem value="verified">Doğrulanmış</MenuItem>
              <MenuItem value="unverified">Doğrulanmamış</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Sırala</InputLabel>
            <Select
              value={sortBy}
              label="Sırala"
              onChange={(e) => setSortBy(e.target.value)}
            >
              <MenuItem value="blockedCount">Engellenme Sayısı</MenuItem>
              <MenuItem value="createdAt">Oluşturulma Tarihi</MenuItem>
              <MenuItem value="eula">EULA Durumu</MenuItem>
              <MenuItem value="emailVerified">E-posta Doğrulama</MenuItem>
            </Select>
          </FormControl>
          <IconButton
            onClick={() => setSortDir(sortDir === "asc" ? "desc" : "asc")}
          >
            {sortDir === "asc" ? <ArrowUpward /> : <ArrowDownward />}
          </IconButton>
        </Box>
        <AddUserModal
          open={addUserModalOpen}
          onClose={() => setAddUserModalOpen(false)}
          onUserAdded={(newUser) => setUsers((prev) => [...prev, newUser])}
        />
        {displayedUsers.length === 0 && !loading ? (
          <Typography>Gösterilecek kullanıcı bulunamadı.</Typography>
        ) : (
          <TableContainer component={Paper} elevation={1}>
            <Table sx={{ minWidth: 650 }} aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell>Avatar</TableCell>
                  <TableCell>Görünen Ad</TableCell>
                  <TableCell>E-posta</TableCell>
                  <TableCell>Rol</TableCell>
                  <TableCell>Oluşturulma Tarihi</TableCell>
                  <TableCell>E-posta Doğrulandı mı?</TableCell>
                  <TableCell>EULA Durumu</TableCell>
                  <TableCell>Engellenme Sayısı</TableCell>
                  <TableCell>Karantina Durumu</TableCell>
                  <TableCell>Eylemler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayedUsers.map((user) => (
                  <TableRow
                    key={user.id}
                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                  >
                    <TableCell>
                      <Avatar
                        src={user.photoURL}
                        alt={user.displayName}
                        onClick={() =>
                          console.log("Starbucks user data:", user)
                        }
                      />
                    </TableCell>
                    <TableCell>{user.displayName || "N/A"}</TableCell>
                    <TableCell>{user.email || "N/A"}</TableCell>
                    <TableCell>
                      <FormControl
                        size="small"
                        sx={{ minWidth: 100 }}
                        disabled={updatingRoles[user.id]}
                      >
                        <InputLabel id={`role-select-label-${user.id}`}>
                          Rol
                        </InputLabel>
                        <Select
                          labelId={`role-select-label-${user.id}`}
                          value={selectedRoles[user.id] || user.role || "user"}
                          onChange={(e) =>
                            handleRoleChange(user.id, e.target.value)
                          }
                          label="Rol"
                        >
                          <MenuItem value="user">User</MenuItem>
                          <MenuItem value="admin">Admin</MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>{formatDate(user.createdAt)}</TableCell>
                    <TableCell>
                      <Tooltip
                        title={
                          user.emailVerified ? "Doğrulanmış" : "Doğrulanmamış"
                        }
                      >
                        <CheckCircle
                          color={user.emailVerified ? "success" : "error"}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const eula =
                          user.eulaAcceptance || user.termsAcceptance; // fallback
                        if (eula) {
                          const acceptedAt = eula.acceptedAt
                            ? new Date(eula.acceptedAt).toLocaleString("tr-TR")
                            : "Bilinmiyor";
                          const ip = eula.ipAddress || "Bilinmiyor";
                          const device = eula.deviceInfo || "Bilinmiyor";
                          const version = eula.termsVersion || "v1.0";
                          return (
                            <Tooltip
                              title={`Kabul Tarihi: ${acceptedAt}\nIP: ${ip}\nCihaz: ${device}`}
                            >
                              <Chip
                                icon={<CheckCircle />}
                                label={`Kabul Edildi (${version})`}
                                color="success"
                                size="small"
                              />
                            </Tooltip>
                          );
                        }
                        return (
                          <Chip
                            label="EULA Bilgisi Yok"
                            color="warning"
                            size="small"
                          />
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={`${user.blockedCount || 0} kişi`}
                        color={
                          user.blockedCount >= 7
                            ? "error"
                            : user.blockedCount > 5
                            ? "warning"
                            : user.blockedCount > 2
                            ? "info"
                            : "default"
                        }
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {user.isQuarantined ? (
                        <Chip
                          label="KARANTİNADA"
                          color="error"
                          size="small"
                          sx={{ fontWeight: "bold" }}
                        />
                      ) : (
                        <Chip
                          label="Aktif"
                          color="success"
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                        <Button
                          variant="outlined"
                          size="small"
                          color="primary"
                          onClick={() =>
                            window.open(`/users/${user.id}`, "_blank")
                          }
                        >
                          Göster
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => handleUpdateRole(user.id)}
                          disabled={
                            updatingRoles[user.id] ||
                            selectedRoles[user.id] === user.role
                          }
                          sx={{ position: "relative", minWidth: 80 }}
                        >
                          {updatingRoles[user.id] ? (
                            <CircularProgress
                              size={20}
                              color="inherit"
                              sx={{ position: "absolute" }}
                            />
                          ) : (
                            "Rol Güncelle"
                          )}
                        </Button>
                        <IconButton
                          size="small"
                          onClick={() => handleEditUser(user)}
                          color="primary"
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteUser(user.id)}
                          color="error"
                        >
                          <Delete />
                        </IconButton>
                        {user.blockedCount > 5 && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            onClick={() => handleDisableUser(user.id)}
                            sx={{ ml: 1 }}
                          >
                            Hesabı Durdur
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Edit User Dialog */}
        <Dialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Kullanıcı Düzenle</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Görünen Ad"
                  value={editFormData.displayName || ""}
                  onChange={(e) =>
                    setEditFormData((prev) => ({
                      ...prev,
                      displayName: e.target.value,
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="E-posta"
                  type="email"
                  value={editFormData.email || ""}
                  onChange={(e) =>
                    setEditFormData((prev) => ({
                      ...prev,
                      email: e.target.value,
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <TextField
                    fullWidth
                    label="Profil Fotoğrafı URL"
                    value={editFormData.photoURL || ""}
                    onChange={(e) =>
                      setEditFormData((prev) => ({
                        ...prev,
                        photoURL: e.target.value,
                      }))
                    }
                  />
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      sx={{ display: "none" }}
                      id="image-upload-input"
                    />
                    <label htmlFor="image-upload-input">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<CloudUpload />}
                        disabled={uploadingImage}
                        size="small"
                      >
                        {uploadingImage ? "Yükleniyor..." : "Resim Yükle"}
                      </Button>
                    </label>
                    <Chip
                      label="veya URL girin"
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Rol</InputLabel>
                  <Select
                    value={editFormData.role || "user"}
                    onChange={(e) =>
                      setEditFormData((prev) => ({
                        ...prev,
                        role: e.target.value,
                      }))
                    }
                    label="Rol"
                  >
                    <MenuItem value="user">User</MenuItem>
                    <MenuItem value="admin">Admin</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>E-posta Doğrulandı</InputLabel>
                  <Select
                    value={editFormData.emailVerified ? "true" : "false"}
                    onChange={(e) =>
                      setEditFormData((prev) => ({
                        ...prev,
                        emailVerified: e.target.value === "true",
                      }))
                    }
                    label="E-posta Doğrulandı"
                  >
                    <MenuItem value="true">Evet</MenuItem>
                    <MenuItem value="false">Hayır</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Block Management and Quarantine */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Yönetim Araçları
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    border: "1px solid #ddd",
                    p: 2,
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="body1">
                    Engellenme Sayısı:{" "}
                    <strong>{editingUser?.blockedCount || 0}</strong>
                  </Typography>
                  <Button
                    variant="outlined"
                    color="warning"
                    size="small"
                    onClick={handleResetBlocks}
                    disabled={
                      updating || (editingUser?.blockedCount || 0) === 0
                    }
                  >
                    Engellemeleri Sıfırla
                  </Button>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={editFormData.isManuallyQuarantined || false}
                      onChange={(e) =>
                        setEditFormData((prev) => ({
                          ...prev,
                          isManuallyQuarantined: e.target.checked,
                        }))
                      }
                      color="error"
                    />
                  }
                  label="Manuel Karantina"
                />
              </Grid>
              {editFormData.photoURL && (
                <Grid item xs={12}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Typography variant="body2">Önizleme:</Typography>
                    <Avatar
                      src={editFormData.photoURL}
                      sx={{ width: 56, height: 56 }}
                    />
                  </Box>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialogOpen(false)}>İptal</Button>
            <Button
              onClick={handleSaveEdit}
              variant="contained"
              disabled={updating}
            >
              {updating ? <CircularProgress size={20} /> : "Kaydet"}
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Box>
  );
}

export default UserManagementPage;
